import * as Yup from "yup";

/**
 * GLOBAL whitespace / blank-field guard.
 *
 * QA kept raising the same defect on module after module: a required text field
 * accepts a single spacebar press and the record saves with a blank-looking
 * name. The cause is that `Yup.string().required()` only rejects "", null and
 * undefined -- "   " is a non-empty string, so it passes. With 433 `.required()`
 * calls across 69 files, fixing that per-screen guarantees the next module ships
 * the bug again.
 *
 * So it is fixed in ONE place, twice over:
 *
 *   1. installWhitespaceGuard() below patches yup's StringSchema so *every*
 *      schema -- existing and future, written by anyone -- treats a
 *      whitespace-only string as absent, and runs its other string tests
 *      (min/max/matches/email) against the trimmed value.
 *
 *   2. deepTrim() is applied to every outgoing request body in
 *      services/BaseService, so padding never reaches the database even from a
 *      screen that does no validation at all.
 *
 * Do NOT add a per-screen `.trim()` rule next to a `.required()` -- it is
 * already covered here. Extend this file instead.
 */

// ---------------------------------------------------------------------------
// 1. yup guard
// ---------------------------------------------------------------------------

/** True for null/undefined and for strings that are empty once trimmed. */
export const isBlank = (value) =>
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim().length === 0);

let guardInstalled = false;

/**
 * Patch yup's string schema in place.
 *
 * Both hooks are *prototype* methods that yup resolves at validation time, not
 * at schema-construction time. That is deliberate: it means this works no
 * matter when it runs relative to the module-level `Yup.object().shape({...})`
 * calls scattered across the views, so it cannot be broken by an import being
 * reordered later.
 */
export const installWhitespaceGuard = () => {
    if (guardInstalled) return;
    guardInstalled = true;

    // `Yup.string()` returns a StringSchema instance; its prototype is the class
    // every string schema in the app shares.
    const stringSchemaProto = Object.getPrototypeOf(Yup.string());

    // `.required()` asks the schema whether the value is "present". Out of the
    // box a string is present when its length > 0, which is what lets "   "
    // through. Blank-after-trim is now absent, so `.required()` reports the
    // screen's own message ("Branch Name is required.") with no extra wiring.
    const originalIsPresent = stringSchemaProto._isPresent;
    stringSchemaProto._isPresent = function (value) {
        if (typeof value === "string" && value.trim().length === 0) {
            return false;
        }
        return originalIsPresent.call(this, value);
    };

    // Every other string test (min, max, matches, email, length) runs against
    // the cast value, so trimming here keeps validation honest about what will
    // actually be stored: " 110011 " is a valid 6-digit zip because BaseService
    // sends "110011". This only affects validation -- Formik's own values are
    // untouched, so the field keeps showing exactly what the user typed.
    const originalCast = stringSchemaProto._cast;
    stringSchemaProto._cast = function (rawValue, options) {
        const value = originalCast.call(this, rawValue, options);
        return typeof value === "string" ? value.trim() : value;
    };
};

// ---------------------------------------------------------------------------
// 2. outgoing-payload trim
// ---------------------------------------------------------------------------

/**
 * Keys left untrimmed. A stored credential may legitimately end in a space, and
 * silently trimming it at the wire would lock that user out of an account they
 * can still sign into today. Whitespace-ONLY values in these fields are still
 * rejected, by the yup guard above.
 */
const UNTRIMMED_KEYS = new Set([
    "password",
    "currentpassword",
    "oldpassword",
    "newpassword",
    "confirmpassword",
    "confirmnewpassword",
]);

/** Binary / streaming bodies must be forwarded byte-for-byte. */
const isOpaqueBody = (value) =>
    (typeof FormData !== "undefined" && value instanceof FormData) ||
    (typeof Blob !== "undefined" && value instanceof Blob) ||
    (typeof File !== "undefined" && value instanceof File) ||
    (typeof URLSearchParams !== "undefined" && value instanceof URLSearchParams) ||
    (typeof ArrayBuffer !== "undefined" && ArrayBuffer.isView(value)) ||
    value instanceof ArrayBuffer;

/**
 * Recursively trim every string in a request payload.
 *
 * Returns a new value; the caller's object is never mutated, so component state
 * that happens to share the object is unaffected.
 */
export const deepTrim = (value, key = "") => {
    if (typeof value === "string") {
        return UNTRIMMED_KEYS.has(String(key).toLowerCase()) ? value : value.trim();
    }

    if (Array.isArray(value)) {
        return value.map((item) => deepTrim(item, key));
    }

    // Dates, Files, Blobs and the like are values, not plain containers.
    if (value === null || typeof value !== "object") return value;
    if (value instanceof Date || isOpaqueBody(value)) return value;
    if (Object.getPrototypeOf(value) !== Object.prototype && Object.getPrototypeOf(value) !== null) {
        return value;
    }

    const trimmed = {};
    for (const [prop, propValue] of Object.entries(value)) {
        trimmed[prop] = deepTrim(propValue, prop);
    }
    return trimmed;
};

/** Trim a request body, unless it is a body we must not touch. */
export const trimRequestBody = (data) => {
    if (data === null || data === undefined) return data;
    // An already-serialised body (a raw JSON string) is passed through: re-parsing
    // it to trim would risk changing the bytes the caller deliberately built.
    if (typeof data === "string") return data;
    if (isOpaqueBody(data)) return data;
    return deepTrim(data);
};

export default installWhitespaceGuard;
